import React, { useRef, useEffect, useState } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { Brain, Code, Layers, Cog, Users, Target, Zap, Shield, TrendingUp, Database } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ANIMATION_VARIANTS, HERO_CONTENT } from '../constants';
import { 
  useFloatingParticles,
  usePerformanceMonitoring,
  useOptimizedBackground
} from '../hooks/useAnimations';
import { GlassCard, BenefitCard, Marquee, StyledIcon, FeatureList } from '../components/animations';
import { handlePageNavigation } from '../utils/navigation';

// AI Development Timeline Component - Customized for AI workflow
const AIWorkflowTimeline: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  
  // Detect screen size
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start 80%", "end 20%"] // Even earlier start for smoother animations
  });

  // Smooth spring animation for performance
  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  // Prevent backtracking - once progress reaches a point, it stays there
  const [maxProgress, setMaxProgress] = useState(0);
  
  useEffect(() => {
    const unsubscribe = smoothProgress.onChange((latest) => {
      if (latest > maxProgress) {
        setMaxProgress(latest);
      }
    });
    return unsubscribe;
  }, [smoothProgress, maxProgress]);

  // Use maxProgress instead of smoothProgress to prevent backtracking
  const pathLength = useTransform(() => Math.min(maxProgress * 3, 1)); // Faster wave drawing
  
  // Individual card visibility - slower, more spaced timing
  const card1Progress = useTransform(() => Math.min(Math.max((maxProgress - 0.05) * 6, 0), 1));
  const card2Progress = useTransform(() => Math.min(Math.max((maxProgress - 0.12) * 6, 0), 1));
  const card3Progress = useTransform(() => Math.min(Math.max((maxProgress - 0.19) * 6, 0), 1));
  const card4Progress = useTransform(() => Math.min(Math.max((maxProgress - 0.26) * 6, 0), 1));
  const card5Progress = useTransform(() => Math.min(Math.max((maxProgress - 0.33) * 6, 0), 1));

  // Calculate responsive positions based on sine wave
  const calculatePosition = (index: number, total: number) => {
    if (isMobile) {
      // Position cards with sine wave in between on mobile
      return {
        x: index % 2 === 0 ? "12%" : "52%",  // Alternate left and right
        y: `${15 + (index * 18)}%`  // Closer vertical spacing
      };
    }
    
    // Desktop: distribute along sine wave
    const segmentWidth = 80 / total; // Use 80% of width for better margins
    const xPercent = 10 + (index * segmentWidth); // Start at 10% for margin
    
    // Sine wave calculation: peaks at odd indices, valleys at even
    const yPercent = index % 2 === 0 ? 15 : 55; // Alternate between peak and valley
    
    return {
      x: `${xPercent}%`,
      y: `${yPercent}%`
    };
  };

  const processSteps = [
    {
      step: "1",
      title: "Discovery",
      description: "Understanding your business objectives, challenges, and data infrastructure to identify AI opportunities.",
      color: "blue",
      gradient: "from-blue-500 to-cyan-600",
      progress: card1Progress
    },
    {
      step: "2", 
      title: "Strategy",
      description: "Creating tailored AI strategy with technology recommendations and implementation approach.",
      color: "purple",
      gradient: "from-purple-500 to-pink-600",
      progress: card2Progress
    },
    {
      step: "3",
      title: "Development", 
      description: "Custom AI software development and solution design tailored to your specific requirements.",
      color: "emerald",
      gradient: "from-emerald-500 to-teal-600",
      progress: card3Progress
    },
    {
      step: "4",
      title: "Deploy",
      description: "Seamless AI deployment and integration into your existing infrastructure and workflows.",
      color: "orange",
      gradient: "from-orange-500 to-red-600",
      progress: card4Progress
    },
    {
      step: "5",
      title: "Optimize",
      description: "Continuous monitoring, support, and optimization for peak AI performance and adaptability.",
      color: "cyan",
      gradient: "from-cyan-500 to-blue-600",
      progress: card5Progress
    }
  ].map((step, index) => ({
    ...step,
    position: calculatePosition(index, 5)
  }));

  return (
    <motion.div 
      ref={containerRef}
      className="mb-20 relative"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.1 }}
      variants={ANIMATION_VARIANTS.staggerChildren}
    >
      <motion.h2 
        className="heading-2 text-white-gradient mb-16 text-center"
        variants={ANIMATION_VARIANTS.slideUp}
      >
        Our AI <span className="text-primary-gradient">Process</span>
      </motion.h2>
      
      {/* Scroll-controlled Timeline Container - Responsive */}
      <div className={`relative overflow-visible ${isMobile ? 'h-[800px]' : 'h-[600px]'} -mx-4 px-4 md:-mx-8 md:px-8 lg:-mx-16 lg:px-16`}>
        
        {
        <motion.svg
          className="absolute inset-0 w-full h-full pointer-events-none"
          viewBox={isMobile ? "0 0 400 1000" : "0 0 1000 400"}
          preserveAspectRatio="xMidYMid meet"
          style={{ zIndex: 1, opacity: isMobile ? 0.4 : 1 }}
        >
          <defs>
            <linearGradient id="aiTimelineGradient" x1={isMobile ? "0%" : "0%"} y1={isMobile ? "0%" : "0%"} x2={isMobile ? "0%" : "100%"} y2={isMobile ? "100%" : "0%"}>
              <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.001" />
              <stop offset="10%" stopColor="#3b82f6" stopOpacity="0.05" />
              <stop offset="20%" stopColor="#3b82f6" stopOpacity="0.6" />
              <stop offset="33%" stopColor="#8b5cf6" stopOpacity="0.9" />
              <stop offset="50%" stopColor="#10b981" stopOpacity="0.8" />
              <stop offset="66%" stopColor="#f59e0b" stopOpacity="0.8" />
              <stop offset="80%" stopColor="#06b6d4" stopOpacity="0.6" />
              <stop offset="90%" stopColor="#06b6d4" stopOpacity="0.05" />
              <stop offset="100%" stopColor="#06b6d4" stopOpacity="0.001" />
            </linearGradient>
            <filter id="aiGlow">
              <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
              <feMerge> 
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
          </defs>
          
          {/* Sine wave - horizontal for desktop, vertical for mobile */}
          <motion.path
            d={isMobile ? "M200 -100Q270 0 200 70q-70 90 0 165t0 165 0 165 0 165 0 165q70 70 0 165" : "M-100 200Q0 270 70 200q90-70 165 0t165 0 165 0 165 0 165 0q70 70 165 0"}            
            stroke="url(#aiTimelineGradient)"
            strokeWidth="6"
            fill="none"
            strokeLinecap="round"
            filter="url(#aiGlow)"
            style={{ pathLength }}
            initial={{ pathLength: 0 }}
          />
          
          {/* Wave position markers for precise card placement - Updated for 5 cards */}
          <motion.circle cx="120" cy="200" r="4" fill="#3b82f6" opacity="0.6" />
          <motion.circle cx="280" cy="200" r="4" fill="#8b5cf6" opacity="0.6" />
          <motion.circle cx="440" cy="200" r="4" fill="#10b981" opacity="0.6" />
          <motion.circle cx="600" cy="200" r="4" fill="#f59e0b" opacity="0.6" />
          <motion.circle cx="760" cy="200" r="4" fill="#06b6d4" opacity="0.6" />
          
          {/* Animated AI particle traveling along path - Compressed for 5 cards */}
          <motion.circle
            cx="-100"
            cy="200"
            r="8"
            fill="#3b82f6"
            style={{
              offsetDistance: smoothProgress,
              offsetPath: "path('M-100,200 Q-20,250 30,200 Q80,150 120,200 Q180,250 240,200 Q280,150 320,200 Q380,250 440,200 Q480,150 520,200 Q580,250 640,200 Q680,150 720,200 Q780,250 840,200')"
            }}
            animate={{
              scale: [1, 1.3, 1],
              opacity: [0.6, 1, 0.6]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        </motion.svg>
        }

        {/* AI Process Step Cards */}
        {processSteps.map((step) => (
          <motion.div
            key={step.step}
            className={`absolute ${isMobile ? 'w-[40%] max-w-[160px]' : 'w-[280px]'}`}
            style={{
              left: step.position.x,
              top: step.position.y,
              transform: "translateX(-50%) translateY(-50%)",
              zIndex: 10,
              opacity: useTransform(step.progress, [0, 0.2, 1], [0, 0.8, 1]),
              scale: useTransform(step.progress, [0, 0.3, 1], [0.8, 0.95, 1]),
              y: useTransform(step.progress, [0, 1], [20, 0]),
            }}
            transition={{
              duration: 0.8,
              ease: [0.25, 0.46, 0.45, 0.94], // Custom bezier for smooth entrance
            }}
          >
            {/* Compact AI-themed Card */}
            <motion.div
              className="relative group"
              whileHover={{ rotateY: 5 }}
              style={{ perspective: "1000px" }}
            >
              <div className={`${isMobile ? 'p-3' : 'w-48 p-4'} rounded-2xl relative overflow-hidden backdrop-blur-xl bg-black/40 border border-white/20 shadow-2xl`}>
                {/* Holographic background effect */}
                <motion.div
                  className={`absolute inset-0 bg-gradient-to-br ${step.gradient} opacity-0 group-hover:opacity-20 transition-opacity duration-500 rounded-2xl`}
                />
                
                {/* Animated border glow */}
                <motion.div
                  className={`absolute inset-0 rounded-2xl border-2 border-transparent bg-gradient-to-br ${step.gradient} opacity-0 group-hover:opacity-30`}
                  style={{ mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)" }}
                  animate={{
                    opacity: [0, 0.3, 0],
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                />
                
                {/* Step number */}
                <motion.div 
                  className="flex items-center justify-center mb-3"
                >
                  <motion.div 
                    className={`${isMobile ? 'w-10 h-10' : 'w-12 h-12'} bg-gradient-to-br from-${step.color}-500/20 to-${step.color}-600/30 rounded-xl border border-${step.color}-400/30 flex items-center justify-center relative overflow-hidden`}
                    whileHover={{ rotate: 15 }}
                    transition={{ duration: 0.4 }}
                  >
                    <div className={`absolute inset-0 bg-gradient-to-br from-${step.color}-400/20 to-${step.color}-600/30 rounded-xl blur-md transition-all duration-500`} />
                    <span className={`text-lg font-bold text-${step.color}-400 relative z-40`}>{step.step}</span>
                  </motion.div>
                </motion.div>
                
                <motion.h3 
                  className={`${isMobile ? 'text-sm' : 'text-base'} font-bold text-white mb-2 text-center group-hover:text-gray-100 transition-colors duration-300`}
                  whileHover={{ y: -1 }}
                >
                  {step.title}
                </motion.h3>
                
                {!isMobile && (
                  <motion.p 
                    className="text-gray-300 text-xs leading-relaxed text-center group-hover:text-gray-200 transition-colors duration-300"
                    initial={{ opacity: 0.8 }}
                    whileHover={{ opacity: 1 }}
                  >
                    {step.description}
                  </motion.p>
                )}
                
                {/* Connection line to wave */}
                <motion.div
                  className={`absolute ${step.position.y === "12%" ? 'bottom-0' : 'top-0'} left-1/2 transform -translate-x-1/2 w-0.5 h-12 bg-gradient-to-${step.position.y === "12%" ? 't' : 'b'} ${step.gradient} opacity-60`}
                  style={{ scaleY: step.progress }}
                />
                
                {/* Digital corner indicators */}
                <div className="absolute top-1 right-1 w-1 h-1 bg-white/40 rounded-full" />
                <div className="absolute bottom-1 left-1 w-1 h-1 bg-white/40 rounded-full" />
                
                {/* Scan line effect */}
                <motion.div
                  className="absolute inset-x-0 h-0.5 bg-gradient-to-r from-transparent via-white/50 to-transparent"
                  animate={{
                    y: [-20, 80, -20]
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "linear"
                  }}
                />
              </div>
            </motion.div>
          </motion.div>
        ))}
        
        {/* Floating AI particles */}
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-cyan-400/60 rounded-full"
            style={{
              left: `${15 + i * 12}%`,
              top: `${25 + Math.sin(i) * 25}%`
            }}
            animate={{
              y: [0, -20, 0],
              opacity: [0.3, 0.8, 0.3],
              scale: [1, 1.5, 1]
            }}
            transition={{
              duration: 3 + i * 0.5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: i * 0.7
            }}
          />
        ))}
      </div>
    </motion.div>
  );
};

const AISoftwareSolutionsPage: React.FC = () => {
  usePerformanceMonitoring('AISoftwareSolutionsPage');
  
  // Global floating particles for the entire page
  const { particlesRef, containerClasses: particlesContainerClasses, particles } = useFloatingParticles(50);

  // Optimized backgrounds for alternating sections
  const { backgroundClasses: heroBackgroundClasses, backgroundStyle: heroBackgroundStyle } = useOptimizedBackground(
    HERO_CONTENT.backgroundImage,
    'linear-gradient(rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0.9))'
  );

  const { backgroundClasses: overviewBackgroundClasses, backgroundStyle: overviewBackgroundStyle } = useOptimizedBackground(
    HERO_CONTENT.backgroundImage,
    'linear-gradient(rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0.9))'
  );

  const { backgroundClasses: servicesBackgroundClasses, backgroundStyle: servicesBackgroundStyle } = useOptimizedBackground(
    HERO_CONTENT.backgroundImage,
    'linear-gradient(rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0.9))'
  );

  const { backgroundClasses: processBackgroundClasses, backgroundStyle: processBackgroundStyle } = useOptimizedBackground(
    HERO_CONTENT.backgroundImage,
    'linear-gradient(rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0.9))'
  );

  const { backgroundClasses: benefitsBackgroundClasses, backgroundStyle: benefitsBackgroundStyle } = useOptimizedBackground(
    HERO_CONTENT.backgroundImage,
    'linear-gradient(rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0.9))'
  );

  const { backgroundClasses: industriesBackgroundClasses, backgroundStyle: industriesBackgroundStyle } = useOptimizedBackground(
    HERO_CONTENT.backgroundImage,
    'linear-gradient(rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0.9))'
  );

  const { backgroundClasses: ctaBackgroundClasses, backgroundStyle: ctaBackgroundStyle } = useOptimizedBackground(
    HERO_CONTENT.backgroundImage,
    'linear-gradient(rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0.9))'
  );

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={ANIMATION_VARIANTS.fadeIn}
      className="min-h-screen relative"
    >
      {/* Global Floating Particles Background */}
      <div ref={particlesRef} className={`${particlesContainerClasses} fixed inset-0 z-30`}>
        {particles.map((particle) => (
          <div
            key={particle.id}
            className={particle.classes}
            style={particle.style}
          />
        ))}
      </div>

      <div className="relative z-40">
        {/* Hero Section */}
        <section className="section-padding relative text-white">
          {/* Hero Background - Normal orientation */}
          <div 
            className={heroBackgroundClasses}
            style={heroBackgroundStyle}
          />
          
          <div className="container relative z-40 pt-32 mb-14">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={ANIMATION_VARIANTS.staggerChildren}
              className="max-w-4xl mx-auto text-center"
            >
              <motion.div 
                className="flex items-center justify-center mb-6"
                variants={ANIMATION_VARIANTS.slideUp}
              >
                <StyledIcon 
                  icon={Brain} 
                  size="xl" 
                  color="primary" 
                  variant="static"
                />
              </motion.div>

              <motion.h1
                variants={ANIMATION_VARIANTS.slideUp}
                className="heading-1 mb-6"
              >
                AI Software <span className="text-primary-gradient">& Solutions</span>
              </motion.h1>
              
              <motion.p
                variants={ANIMATION_VARIANTS.slideUp}
                className="hero-subtitle text-center mb-12 max-w-3xl mx-auto"
              >
                Transform your business with cutting-edge artificial intelligence through our comprehensive AI Software and Solutions. 
                We specialize in delivering end-to-end AI services, from strategic consulting and custom software development 
                to fully integrated solutions and deployment.
              </motion.p>
            </motion.div>
          </div>
        </section>

        {/* Service Overview Section */}
        <section className="section-padding relative text-white">
          {/* Overview Background - Rotated orientation for alternating effect */}
          <div 
            className={overviewBackgroundClasses}
            style={{
              ...overviewBackgroundStyle,
              transform: 'translateZ(0) rotate(180deg)',
              backgroundPosition: 'bottom',
            }}
          />
          
          <div className="container relative z-40">
            <GlassCard className="p-8 lg:p-12 rounded-3xl">
              <div className="grid lg:grid-cols-2 gap-16 items-center">
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                  variants={ANIMATION_VARIANTS.staggerChildren}
                >
                  <motion.h2 
                    className="text-3xl lg:text-4xl font-bold mb-6"
                    variants={ANIMATION_VARIANTS.slideUp}
                  >
                    AI-Powered <span className="text-primary-gradient">Innovation</span> for Your Business
                  </motion.h2>
                  <motion.p 
                    className="text-lg text-gray-300 mb-8 leading-relaxed"
                    variants={ANIMATION_VARIANTS.slideUp}
                  >
                    Whether you're looking to leverage AI to improve operations, enhance customer experiences, or drive innovation, 
                    our team of AI experts is here to guide you every step of the way with tailored solutions that deliver measurable results.
                  </motion.p>
                  
                  <motion.div 
                    className="mb-8"
                    variants={ANIMATION_VARIANTS.staggerChildren}
                  >
                    <FeatureList 
                      features={[
                        "Strategic AI consultation and roadmap development",
                        "Custom AI software development and solutions",
                        "Seamless deployment and integration services",
                        "Ongoing support and optimization"
                      ]}
                      animated={false}
                    />
                  </motion.div>
                </motion.div>
                
                <motion.div 
                  className="relative"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                  variants={ANIMATION_VARIANTS.slideUp}
                >
                  <div className="relative overflow-hidden rounded-2xl shadow-2xl">
                    <img 
                      src="/assets/images/services/ai-solutions.png"
                      alt="AI Software Solutions"
                      className="w-full h-80 object-cover"
                    />
                    {/* Overlay gradient for better text contrast */}
                    <div className="absolute inset-0 bg-gradient-to-tr from-black/20 via-transparent to-primary-500/10"></div>
                    
                    {/* AI Innovation badge */}
                    <div className="absolute top-4 left-4 bg-black/60 backdrop-blur-sm rounded-lg px-3 py-2 border border-primary-400/30">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                        <span className="text-white text-xs font-medium">AI Innovation</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </GlassCard>
          </div>
        </section>

        {/* Key Features Section */}
        <section className="section-padding relative text-white">
          {/* Services Background - Normal orientation */}
          <div 
            className={servicesBackgroundClasses}
            style={servicesBackgroundStyle}
          />
          
          <div className="container relative z-40">
            <motion.div 
              className="mb-20"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={ANIMATION_VARIANTS.staggerChildren}
            >
              <motion.h2 
                className="heading-2 mb-16 text-center"
                variants={ANIMATION_VARIANTS.slideUp}
              >
                Key <span className="text-primary-gradient">Features</span>
              </motion.h2>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                <BenefitCard
                  icon={Target}
                  title="Strategic Consulting"
                  description="A deep dive into your business processes and data, with actionable insights on how AI can be leveraged for growth and competitive advantage."
                  color="blue"
                  delay={0.1}
                />
                <BenefitCard
                  icon={Code}
                  title="Custom AI Software"
                  description="Development of tailor-made AI solutions designed for your specific industry, challenges, and objectives with cutting-edge technologies."
                  color="emerald"
                  delay={0.2}
                />
                <BenefitCard
                  icon={Database}
                  title="AI Model Training & Optimization"
                  description="End-to-end development of machine learning and deep learning models, from training to fine-tuning for optimal performance."
                  color="cyan"
                  delay={0.3}
                />
                <BenefitCard
                  icon={Layers}
                  title="Seamless Deployment"
                  description="Complete deployment services, ensuring AI models are integrated seamlessly into your business environment for maximum efficiency."
                  color="purple"
                  delay={0.4}
                />
                <BenefitCard
                  icon={Shield}
                  title="Ongoing Monitoring & Support"
                  description="Continuous monitoring, maintenance, and optimization of AI systems to ensure high performance and adaptability as your business evolves."
                  color="orange"
                  delay={0.5}
                />
                <BenefitCard
                  icon={TrendingUp}
                  title="Data-Driven Results"
                  description="We ensure that the AI solutions we develop deliver real, measurable outcomes by leveraging your business data to optimize processes and predict trends."
                  color="green"
                  delay={0.6}
                />
              </div>
            </motion.div>
          </div>
        </section>

        {/* Our AI Process Section */}
        <section className="section-padding relative text-white">
          {/* Process Background - Rotated orientation */}
          <div 
            className={processBackgroundClasses}
            style={{
              ...processBackgroundStyle,
              transform: 'translateZ(0) rotate(180deg)',
              backgroundPosition: 'bottom',
            }}
          />
          
          <div className="container relative z-40">
            <AIWorkflowTimeline />
          </div>
        </section>

        {/* Our AI Services Section */}
        <section className="section-padding relative text-white">
          {/* Benefits Background - Normal orientation */}
          <div 
            className={benefitsBackgroundClasses}
            style={benefitsBackgroundStyle}
          />
          
          <div className="container relative z-40">
            <motion.div 
              className="mb-20"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={ANIMATION_VARIANTS.staggerChildren}
            >
              <motion.h2 
                className="heading-2 mb-16 text-center"
                variants={ANIMATION_VARIANTS.slideUp}
              >
                Our AI <span className="text-primary-gradient">Services</span>
              </motion.h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8">
                <BenefitCard
                  icon={Brain}
                  title="AI Consultation"
                  description="Our AI experts work with your team to identify opportunities for AI-driven growth and efficiency. We analyze your business needs, assess your data infrastructure, and provide tailored recommendations on how AI can deliver measurable results."
                  color="blue"
                  delay={0.1}
                />
                <BenefitCard
                  icon={Code}
                  title="Custom AI Software Development"
                  description="We design and build AI-powered software tailored specifically to your business needs. From AI-driven automation tools to advanced machine learning models, our solutions enhance productivity and reduce operational costs."
                  color="emerald"
                  delay={0.2}
                />
                <BenefitCard
                  icon={Layers}
                  title="AI Solutions"
                  description="We offer pre-built, ready-to-deploy AI solutions that solve common business challenges, such as customer service chatbots, AI-based analytics platforms, and intelligent recommendation systems."
                  color="cyan"
                  delay={0.3}
                />
                <BenefitCard
                  icon={Cog}
                  title="AI Deployment"
                  description="We ensure the seamless integration and deployment of AI models and solutions into your existing technology infrastructure, whether on-premises, in the cloud, or through hybrid models."
                  color="purple"
                  delay={0.4}
                />
              </div>
            </motion.div>
          </div>
        </section>

        {/* Industries We Serve Section */}
        <section className="section-padding relative text-white">
          {/* Industries Background - Rotated orientation */}
          <div 
            className={industriesBackgroundClasses}
            style={{
              ...industriesBackgroundStyle,
              transform: 'translateZ(0) rotate(180deg)',
              backgroundPosition: 'bottom',
            }}
          />
          
          <div className="container relative z-40">
            <motion.div 
              className="mb-20"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={ANIMATION_VARIANTS.staggerChildren}
            >
              <motion.h2 
                className="heading-2 mb-16 text-center"
                variants={ANIMATION_VARIANTS.slideUp}
              >
                Industries <span className="text-primary-gradient">We Serve</span>
              </motion.h2>
              
              <motion.div 
                className="relative"
                variants={ANIMATION_VARIANTS.slideUp}
              >
                <Marquee speed="normal" expandOnHover={true} className="py-8">
                  <div className="flex space-x-8 flex-shrink-0 pr-8">
                    <div className="w-80 flex-shrink-0">
                      <div className="h-full">
                        <GlassCard className="p-8 rounded-2xl h-full relative overflow-hidden border border-white/10 backdrop-blur-sm">
                          <div className="absolute inset-0 bg-gradient-to-br from-gray-800/30 via-gray-900/20 to-black/30" />
                          <div className="absolute inset-0 bg-gradient-to-tr from-emerald-600/5 to-emerald-400/10 opacity-0 hover:opacity-100 transition-opacity duration-700" />
                          
                          <div className="relative z-40 text-center flex flex-col items-center h-full">
                            <div className="mb-6">
                              <StyledIcon 
                                icon={Shield} 
                                size="md" 
                                color="emerald" 
                                variant="default"
                              />
                            </div>
                            <h3 className="text-xl font-bold text-white mb-4">Healthcare</h3>
                            <p className="text-gray-300 leading-relaxed flex-grow">
                              AI-powered diagnostics, predictive analytics, and personalized treatment plans to improve patient outcomes and operational efficiency.
                            </p>
                          </div>
                        </GlassCard>
                      </div>
                    </div>
                    <div className="w-80 flex-shrink-0">
                      <div className="h-full">
                        <GlassCard className="p-8 rounded-2xl h-full relative overflow-hidden border border-white/10 backdrop-blur-sm">
                          <div className="absolute inset-0 bg-gradient-to-br from-gray-800/30 via-gray-900/20 to-black/30" />
                          <div className="absolute inset-0 bg-gradient-to-tr from-blue-600/5 to-blue-400/10 opacity-0 hover:opacity-100 transition-opacity duration-700" />
                          
                          <div className="relative z-40 text-center flex flex-col items-center h-full">
                            <div className="mb-6">
                              <StyledIcon 
                                icon={TrendingUp} 
                                size="md" 
                                color="blue" 
                                variant="default"
                              />
                            </div>
                            <h3 className="text-xl font-bold text-white mb-4">Finance</h3>
                            <p className="text-gray-300 leading-relaxed flex-grow">
                              Fraud detection, risk management, and AI-driven financial modeling to enhance security and decision-making processes.
                            </p>
                          </div>
                        </GlassCard>
                      </div>
                    </div>
                    <div className="w-80 flex-shrink-0">
                      <div className="h-full">
                        <GlassCard className="p-8 rounded-2xl h-full relative overflow-hidden border border-white/10 backdrop-blur-sm">
                          <div className="absolute inset-0 bg-gradient-to-br from-gray-800/30 via-gray-900/20 to-black/30" />
                          <div className="absolute inset-0 bg-gradient-to-tr from-purple-600/5 to-purple-400/10 opacity-0 hover:opacity-100 transition-opacity duration-700" />
                          
                          <div className="relative z-40 text-center flex flex-col items-center h-full">
                            <div className="mb-6">
                              <StyledIcon 
                                icon={Users} 
                                size="md" 
                                color="purple" 
                                variant="default"
                              />
                            </div>
                            <h3 className="text-xl font-bold text-white mb-4">Retail & E-commerce</h3>
                            <p className="text-gray-300 leading-relaxed flex-grow">
                              Personalized recommendations, inventory management, and customer service automation to boost sales and customer satisfaction.
                            </p>
                          </div>
                        </GlassCard>
                      </div>
                    </div>
                    <div className="w-80 flex-shrink-0">
                      <div className="h-full">
                        <GlassCard className="p-8 rounded-2xl h-full relative overflow-hidden border border-white/10 backdrop-blur-sm">
                          <div className="absolute inset-0 bg-gradient-to-br from-gray-800/30 via-gray-900/20 to-black/30" />
                          <div className="absolute inset-0 bg-gradient-to-tr from-orange-600/5 to-orange-400/10 opacity-0 hover:opacity-100 transition-opacity duration-700" />
                          
                          <div className="relative z-40 text-center flex flex-col items-center h-full">
                            <div className="mb-6">
                              <StyledIcon 
                                icon={Cog} 
                                size="md" 
                                color="orange" 
                                variant="default"
                              />
                            </div>
                            <h3 className="text-xl font-bold text-white mb-4">Manufacturing</h3>
                            <p className="text-gray-300 leading-relaxed flex-grow">
                              Predictive maintenance, quality control, and process optimization through AI-driven automation and monitoring systems.
                            </p>
                          </div>
                        </GlassCard>
                      </div>
                    </div>
                    <div className="w-80 flex-shrink-0">
                      <div className="h-full">
                        <GlassCard className="p-8 rounded-2xl h-full relative overflow-hidden border border-white/10 backdrop-blur-sm">
                          <div className="absolute inset-0 bg-gradient-to-br from-gray-800/30 via-gray-900/20 to-black/30" />
                          <div className="absolute inset-0 bg-gradient-to-tr from-cyan-600/5 to-cyan-400/10 opacity-0 hover:opacity-100 transition-opacity duration-700" />
                          
                          <div className="relative z-40 text-center flex flex-col items-center h-full">
                            <div className="mb-6">
                              <StyledIcon 
                                icon={Zap} 
                                size="md" 
                                color="cyan" 
                                variant="default"
                              />
                            </div>
                            <h3 className="text-xl font-bold text-white mb-4">Telecommunications</h3>
                            <p className="text-gray-300 leading-relaxed flex-grow">
                              AI-based customer service solutions, network optimization, and churn prediction to improve service delivery and retention.
                            </p>
                          </div>
                        </GlassCard>
                      </div>
                    </div>
                    <div className="w-80 flex-shrink-0">
                      <div className="h-full">
                        <GlassCard className="p-8 rounded-2xl h-full relative overflow-hidden border border-white/10 backdrop-blur-sm">
                          <div className="absolute inset-0 bg-gradient-to-br from-gray-800/30 via-gray-900/20 to-black/30" />
                          <div className="absolute inset-0 bg-gradient-to-tr from-green-600/5 to-green-400/10 opacity-0 hover:opacity-100 transition-opacity duration-700" />
                          
                          <div className="relative z-40 text-center flex flex-col items-center h-full">
                            <div className="mb-6">
                              <StyledIcon 
                                icon={Brain} 
                                size="md" 
                                color="green" 
                                variant="default"
                              />
                            </div>
                            <h3 className="text-xl font-bold text-white mb-4">Technology</h3>
                            <p className="text-gray-300 leading-relaxed flex-grow">
                              Advanced AI solutions for software optimization, automated testing, intelligent data processing, and next-generation product development.
                            </p>
                          </div>
                        </GlassCard>
                      </div>
                    </div>
                  </div>
                </Marquee>
                
                {/* Gradient overlays for seamless effect */}
                <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-black/20 to-transparent pointer-events-none z-40"></div>
                <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-black/20 to-transparent pointer-events-none z-40"></div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="section-padding relative text-white">
          {/* CTA Background - Normal orientation */}
          <div 
            className={ctaBackgroundClasses}
            style={ctaBackgroundStyle}
          />
          
          <div className="container relative z-40">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={ANIMATION_VARIANTS.staggerChildren}
              className="text-center max-w-4xl mx-auto"
            >
              <motion.h2 
                className="heading-2 mb-6"
                variants={ANIMATION_VARIANTS.slideUp}
              >
                Ready to Harness the <span className="text-primary-gradient">Power of AI?</span>
              </motion.h2>
              <motion.p 
                className="body-large mb-8"
                variants={ANIMATION_VARIANTS.slideUp}
              >
                Contact us today to schedule a consultation and see how our AI Consultation, Software, Solutions, 
                and Deployment Service can drive innovation and growth in your business!
              </motion.p>
              <motion.div 
                className="text-center"
                variants={ANIMATION_VARIANTS.slideUp}
              >
                <div className="flex flex-col sm:flex-row gap-4 mt-auto justify-center items-center">
                    <Link
                        to="/contact"
                        className="btn-futuristic"
                        onClick={handlePageNavigation}
                    >
                        Get Started with AI
                    </Link>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>
      </div>
    </motion.div>
  );
};

export default AISoftwareSolutionsPage;
